import React from 'react';
import { CheckCircle2, Image as ImageIcon, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const resolvePhotoUrl = (photo) => {
  if (typeof photo === 'string') return photo;
  return photo?.data_url || photo?.url || photo?.photo_url || '';
};

export default function SelectablePhotoGrid({
  photos = [],
  onToggle,
  onDelete,
  onPreview,
  photoDeleteLoading = {},
  columnsClassName = 'grid grid-cols-3 gap-3',
  emptyMessage = 'No photos available yet',
  showDeleteActions = true,
}) {
  if (!photos.length) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-12 text-muted-foreground bg-muted/20">
        <ImageIcon className="mb-4 h-12 w-12 opacity-50" />
        <p className="text-sm">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={columnsClassName}>
      {photos.map((photo, idx) => {
        const photoId = typeof photo === 'object' ? photo.photo_id : null;
        const photoUrl = resolvePhotoUrl(photo);
        const selected = typeof photo === 'object' ? photo.selected !== false : false;
        const isDeleting = !!(photoId && photoDeleteLoading[photoId]);

        return (
          <div
            key={photoId || idx}
            className={cn(
              'group relative aspect-square cursor-pointer overflow-hidden rounded-lg border-2 transition-all duration-200',
              selected
                ? 'border-coral ring-4 ring-coral/15 shadow-md'
                : 'border-border/60 bg-slate-100 hover:border-slate-400',
              isDeleting && 'cursor-wait opacity-70',
            )}
            onClick={() => {
              if (isDeleting) return;
              onToggle?.(photoId, idx);
            }}
          >
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={typeof photo === 'object' ? (photo.caption || `Photo ${idx + 1}`) : `Photo ${idx + 1}`}
                className={cn(
                  'h-full w-full object-cover transition-all duration-200',
                  selected ? 'scale-[1.01]' : 'opacity-80 saturate-75 group-hover:opacity-95 group-hover:saturate-90',
                )}
              />
            ) : null}

            <div className={cn(
              'absolute inset-0 transition-colors',
              selected ? 'bg-coral/10' : 'bg-slate-900/0 group-hover:bg-slate-900/5',
            )}
            />

            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-end p-2">
              {selected ? (
                <div className="inline-flex items-center gap-1 rounded-full bg-coral px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>Selected</span>
                </div>
              ) : (
                <div className="h-6 w-6 rounded-full border border-gray-200 bg-white/90 text-transparent shadow-sm">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
              )}
            </div>

            {onPreview ? (
              <button
                type="button"
                className="absolute top-2 right-2 flex h-8 items-center justify-center gap-1 rounded-full border border-white/30 bg-slate-950/80 px-2.5 text-white opacity-0 scale-95 shadow-sm backdrop-blur-sm transition-all group-hover:opacity-100 group-hover:scale-100 group-hover:bg-slate-950/90 group-hover:shadow-md"
                onClick={(event) => {
                  event.stopPropagation();
                  onPreview({
                    ...(typeof photo === 'object' ? photo : {}),
                    url: photoUrl,
                    index: idx,
                    selected,
                  });
                }}
              >
                <ImageIcon className="h-3 w-3" />
                <span className="text-[11px] font-medium">Preview</span>
              </button>
            ) : null}

            {showDeleteActions && photoId && onDelete ? (
              <button
                type="button"
                className="absolute top-2 left-2 rounded-full bg-red-500/80 p-1.5 text-white opacity-0 transition-all hover:bg-red-600 group-hover:opacity-100"
                disabled={isDeleting}
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete(photoId);
                }}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
